// AdminWorkspaces.tsx — /admin (default tab). Spec §4: name, owner email, plan chip
// (+ "cancels at period end" hint), bots count, messages/pages this month vs plan limits,
// created date, per-row "View as" action. Admin v2 (spec
// docs/superpowers/specs/2026-08-06-admin-v2-internal-workspaces-design.md §A/§B1) adds an
// INTERNAL chip on internal rows and a name link to the new workspace detail page.
// workspace-logo-and-admin-members cycle §B adds a Members column (right after Bots).
//
// "View as" impersonation goes through `../lib/admin-impersonate.ts` (extracted there in this
// task so the bot detail page's "Open owner's view" can reuse the same flow) — it resolves the
// owner's id via the admin plugin's own `listUsers` lookup (exact-match on email) since
// `AdminWorkspaceRow` carries `ownerEmail` only, no `ownerUserId`.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { PLAN_LIMITS } from '@repo/shared-types';
import type { AdminCrawlSignalDto, AdminWorkspacesOverviewDto } from '@repo/shared-types';
import { Badge } from '@repo/ui/components/badge';
import { Button } from '@repo/ui/components/button';
import { Card } from '@repo/ui/components/card';
import { SkeletonText } from '@repo/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo/ui/components/table';
import { AdminPagination } from '../components/AdminPagination';
import { impersonateOwnerByEmail } from '../lib/admin-impersonate';
import { apiFetch } from '../lib/api';
import { useLocale } from '../locales/context';
import { formatDate as formatDateIntl, formatNumber } from '../locales/format';

/** Table shape at the real 44px row height (handoff Shell Anatomy §06) — this list's columns
 * vary in width but not in count, so four generic rows read as "a table is coming" without
 * pretending to preview real values. */
function TableSkeleton() {
  const widths = ['w-[88%]', 'w-[72%]', 'w-[80%]', 'w-[64%]'];
  return (
    <div className="flex flex-col overflow-hidden rounded-lg border">
      <div className="h-[34px] bg-muted" />
      {widths.map((w, i) => (
        <div key={w} className={`flex h-11 items-center px-4 ${i === 0 ? '' : 'border-t'}`}>
          <SkeletonText className={w} />
        </div>
      ))}
    </div>
  );
}

/** Task 6 of the source-detail-diagnostics cycle (design spec "Admin signal") — two numbers,
 * deliberately not a chart/dashboard/alert, whose only job is telling Johnny when the $5/mo
 * Cloudflare Workers Paid upgrade is worth making. Labeled for what they DECIDE, not what
 * they measure: a raw "N truncated_capacity runs" figure is decoration no one can act on (a
 * retried source inflates it, a crashed process is invisible to it) — see
 * `AdminCrawlSignalDto`'s doc comment (`@repo/shared-types`) for the full reasoning behind
 * both numbers.
 *
 * We are on Workers FREE today (docs/design/001-PRD.md §3, "Workers Free (what we are on
 * today)") — the whole premise of this signal is that we have NOT bought Paid yet. Review
 * fix round 1, Important 1: the first draft's "leans on Cloudflare's paid Browser Rendering
 * tier" label read as though we already pay for it, directly contradicting the second card's
 * own "time for Workers Paid" — say "Browser Rendering" without "paid" anywhere in this
 * component. */
function CrawlSignalPanel({ signal }: { signal: AdminCrawlSignalDto }) {
  const { t, locale } = useLocale();
  const { renderedModeSources, capacityTruncatedSources7d } = signal;
  return (
    <Card className="flex flex-col gap-4 p-4 sm:flex-row sm:gap-10">
      <div className="flex flex-1 flex-col gap-1">
        <span className="text-xs font-medium text-muted-foreground">
          {t.admin.workspaces.crawlSignalRenderedLabel}
        </span>
        <span className="text-2xl font-semibold">
          {formatNumber(renderedModeSources.sourceCount, locale)}
          <span className="ml-1.5 text-sm font-normal text-muted-foreground">
            {t.admin.workspaces.crawlSignalSourcesAcrossBots({
              sourceCount: renderedModeSources.sourceCount,
              botCountLabel: formatNumber(renderedModeSources.botCount, locale),
              botCount: renderedModeSources.botCount,
            })}
          </span>
        </span>
        <span className="text-xs text-muted-foreground">
          {t.admin.workspaces.crawlSignalRenderedFootnote}
        </span>
      </div>
      <div className="flex flex-1 flex-col gap-1">
        <span className="text-xs font-medium text-muted-foreground">
          {t.admin.workspaces.crawlSignalCapacityLabel}
        </span>
        <span className="text-2xl font-semibold">
          {formatNumber(capacityTruncatedSources7d.sourceCount, locale)}
          <span className="ml-1.5 text-sm font-normal text-muted-foreground">
            {t.admin.workspaces.crawlSignalSourceCountLabel({ n: capacityTruncatedSources7d.sourceCount })}
          </span>
        </span>
        <span className="text-xs text-muted-foreground">
          {t.admin.workspaces.crawlSignalCapacityFootnote}
        </span>
      </div>
    </Card>
  );
}

export function AdminWorkspaces() {
  const { t, locale } = useLocale();
  const [page, setPage] = useState(1);
  const [rowError, setRowError] = useState<{ id: string; message: string } | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const { data, isPending, error } = useQuery({
    queryKey: ['admin', 'workspaces', page],
    queryFn: () =>
      apiFetch<AdminWorkspacesOverviewDto>(`/api/admin/workspaces?page=${page}`),
  });

  async function handleViewAs(workspaceId: string, ownerEmail: string) {
    setRowError(null);
    setPendingId(workspaceId);
    try {
      await impersonateOwnerByEmail(ownerEmail, t);
    } catch (err) {
      setRowError({
        id: workspaceId,
        message: err instanceof Error ? err.message : t.admin.workspaces.viewAsErrorFallback,
      });
      setPendingId(null);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {isPending && <TableSkeleton />}
      {error && <p className="text-destructive">{error.message}</p>}

      {/* `data?.signal` (not `data &&`): the dashboard (CF Pages) and the API (Railway) deploy
          independently, so a dashboard deploy landing first can hit an API that hasn't shipped
          `signal` yet. Guarding only this panel means that response still renders the rest of
          the page instead of blanking the whole screen (review fix round 1, Minor 4). */}
      {data?.signal && <CrawlSignalPanel signal={data.signal} />}

      {data && data.items.length === 0 && (
        <p className="text-muted-foreground">{t.admin.workspaces.emptyText}</p>
      )}

      {data && data.items.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t.admin.workspaces.colName}</TableHead>
              <TableHead>{t.admin.workspaces.colOwner}</TableHead>
              <TableHead>{t.admin.workspaces.colPlan}</TableHead>
              <TableHead>{t.admin.workspaces.colBots}</TableHead>
              <TableHead>{t.admin.workspaces.colMembers}</TableHead>
              <TableHead>{t.admin.workspaces.colMsgsPerMo}</TableHead>
              <TableHead>{t.admin.workspaces.colPages}</TableHead>
              <TableHead>{t.admin.workspaces.colCreated}</TableHead>
              <TableHead>{t.admin.workspaces.colActions}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.map((ws) => {
              const caps = PLAN_LIMITS[ws.plan];
              return (
                <TableRow key={ws.id}>
                  <TableCell className="font-medium">
                    <Link to={`/admin/workspaces/${ws.id}`} className="hover:underline">
                      {ws.name}
                    </Link>
                  </TableCell>
                  {/* Long owner emails (plus-addressed QA accounts) were pushing the table into horizontal
                      scroll once the Members column landed — truncate with the full value on hover. */}
                  <TableCell className="max-w-[200px] truncate" title={ws.ownerEmail ?? undefined}>
                    {ws.ownerEmail ?? '—'}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-0.5">
                      {ws.internal ? (
                        <Badge>{t.admin.workspaces.internalBadge}</Badge>
                      ) : (
                        <Badge variant="secondary">{caps.name}</Badge>
                      )}
                      {ws.cancelAtPeriodEnd && (
                        <span className="text-xs" style={{ color: 'var(--st-paused)' }}>
                          {t.admin.workspaces.cancelsAtPeriodEndHint}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{ws.botsCount}</TableCell>
                  <TableCell>{ws.membersCount}</TableCell>
                  <TableCell>
                    {formatNumber(ws.messagesThisMonth, locale)}
                    {!ws.internal && ` / ${formatNumber(caps.maxMessages, locale)}`}
                  </TableCell>
                  <TableCell>
                    {formatNumber(ws.pagesTotal, locale)}
                    {!ws.internal && ` / ${formatNumber(caps.maxPages, locale)}`}
                  </TableCell>
                  <TableCell>{formatDateIntl(ws.createdAt, locale, {})}</TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={!ws.ownerEmail || pendingId === ws.id}
                        title={ws.ownerEmail ? undefined : t.admin.workspaces.noOwnerTitle}
                        onClick={() => ws.ownerEmail && handleViewAs(ws.id, ws.ownerEmail)}
                      >
                        {pendingId === ws.id
                          ? t.admin.workspaces.switchingCta
                          : t.admin.workspaces.viewAsCta}
                      </Button>
                      {rowError?.id === ws.id && (
                        <span className="text-xs text-destructive">{rowError.message}</span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {data && <AdminPagination meta={data} onPageChange={setPage} />}
    </div>
  );
}
